import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBundleProps } from 'signer-app/bundle-props';

export type AccessCodeResponse =
  | 'need-access-code'
  | 'invalid-access-code'
  | 'too-many-retries'
  | 'need-sms-auth'
  | 'sms-rate-limit';

export type SignerAuthState = {
  authState: AccessCodeResponse | null;
  setAuthState: React.Dispatch<React.SetStateAction<AccessCodeResponse | null>>;
  accessCode: string | null;
  setAccessCode: (code: string | null) => void;
  hasSetAccessCode: boolean;
  smsMeta: any | null;
  setSmsMeta: (meta: any | null) => void;
  numAttemptsLeft: number;
  setNumAttemptsLeft: (num: number) => void;
  clearAuth: () => void;
};

export function useSignerAuth(): SignerAuthState {
  const queryClient = useQueryClient();
  const { tsmGuid } = useBundleProps();
  const [accessCode, setAccessCodeRaw] = React.useState<string | null>(null);
  const previousAccessCodeRef = React.useRef<string | null>(null);
  const [authState, setAuthState] = React.useState<AccessCodeResponse | null>(
    null,
  );
  const [numAttemptsLeft, setNumAttemptsLeft] = React.useState<number>(0);
  const [smsMeta, setSmsMeta] = React.useState<any | null>(null);

  const clearAuth = React.useCallback(() => {
    setAuthState(null);
    setSmsMeta(null);
    setAccessCodeRaw(null);
  }, []);

  const setAccessCode = React.useCallback(
    (code: string | null) => {
      const isSameCode = code === previousAccessCodeRef.current;
      previousAccessCodeRef.current = code;
      setAccessCodeRaw(code);

      // Same access code resubmitted - invalidate ready query to refetch
      // This ensures retry count is updated and we get a fresh response
      if (isSameCode && code !== null) {
        // Note: The ready query key is ['signer-experience', 'ready', tsmGuid, accessCode]
        // By invalidating with a prefix (omitting accessCode), we match any ready query
        // for this tsmGuid, regardless of accessCode value. This is React Query's default
        // prefix matching behavior.
        queryClient.invalidateQueries({
          queryKey: ['signer-experience', 'ready', tsmGuid],
        });
      }
    },
    [queryClient, tsmGuid],
  );

  return {
    authState,
    setAuthState,
    accessCode,
    setAccessCode,
    hasSetAccessCode: accessCode !== null,
    smsMeta,
    setSmsMeta,
    numAttemptsLeft,
    setNumAttemptsLeft,
    clearAuth,
  };
}
